import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { href } from 'react-router';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '@valwin/facto/components';

import { useOAuthPopup } from '~shared/lib/oauth';
import { ROUTES } from '~shared/routes';

import { initiateInstagramOAuth } from '../api';
import {
  type InstagramOAuthMessage,
  isInstagramOAuthMessage,
} from '../oauth/messages';
import { instagramQueries } from './queries';

export function useConnectInstagram(pharmacyId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const cancelCount = useRef(0);

  const { open } = useOAuthPopup<InstagramOAuthMessage>({
    validate: isInstagramOAuthMessage,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const { origin } = globalThis.location;
      const successUrl = `${origin}${href(ROUTES.integrations.instagramOAuthSuccess, { pharmacyId })}`;
      const errorUrl = `${origin}${href(ROUTES.integrations.instagramOAuthError, { pharmacyId })}`;

      const result = await open({
        getUrl: () =>
          initiateInstagramOAuth(pharmacyId, { successUrl, errorUrl }).then(
            (response) => response.authUrl
          ),
      });

      if (result?.status === 'error') {
        throw new Error('Instagram OAuth flow failed');
      }

      return result;
    },
    onSuccess: (result) => {
      if (result === null) {
        cancelCount.current += 1;

        return;
      }

      queryClient.invalidateQueries({
        queryKey: instagramQueries.account(pharmacyId).queryKey,
      });
      toast.success(t('toast accountConnected'), {
        description: t('toast instagramAccountConnected description'),
      });
    },
    onError: () => {
      cancelCount.current += 1;
      toast.error(t('toast error'), {
        description: t('toast instagramAccountConnecting error description'),
      });
    },
  });

  return { ...mutation, cancelCount };
}
